import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Form, Modal, Spinner } from "react-bootstrap";
import { FaCheckCircle } from "react-icons/fa";
import {
  getSchoolRoles,
  getTeachersBySchoolId,
  updateTeacherProfileWithEmail
} from "../services/TeacherAPIClient";
import "./UpdateTeacherRolesModal.css";

const normaliseText = (value) =>
  String(value || "")
    .trim()
    .replace(/\s+/g, " ")
    .toLowerCase();

const getRoleName = (role) =>
  typeof role === "string"
    ? role
    : role?.name || role?.label || role?.roleName || role?.role_name || "";

const getRoleList = (value) => {
  if (Array.isArray(value)) {
    return value.flatMap(getRoleList);
  }

  if (!value) {
    return [];
  }

  return String(value)
    .split(/[\n,]/)
    .map((role) => role.trim())
    .filter(Boolean);
};

const uniqueRoleNames = (roleNames) => {
  const seen = new Set();

  return roleNames.filter((roleName) => {
    const key = normaliseText(roleName);

    if (!key || seen.has(key)) {
      return false;
    }

    seen.add(key);
    return true;
  });
};

const normalisePrefill = (prefill) => ({
  teacherName: String(prefill?.teacherName || prefill?.teacher_name || "").trim(),
  roleName: String(prefill?.roleName || prefill?.role_name || "").trim(),
  roleNames: uniqueRoleNames([
    ...getRoleList(prefill?.roleName || prefill?.role_name),
    ...getRoleList(prefill?.roleNames || prefill?.role_names)
  ]),
  currentRoleName: String(prefill?.currentRoleName || prefill?.current_role_name || "").trim(),
  removeRoleNames: uniqueRoleNames(getRoleList(prefill?.removeRoleNames || prefill?.remove_role_names))
});

const normaliseTeacher = (teacher) => {
  const roleValues = [
    ...(Array.isArray(teacher?.additionalRoles) ? teacher.additionalRoles : []),
    ...(Array.isArray(teacher?.roles) ? teacher.roles.map(getRoleName) : []),
    teacher?.roleLabel,
    teacher?.role
  ]
    .map(getRoleName)
    .filter(Boolean);

  return {
    ...teacher,
    id: teacher?.id || teacher?._id,
    name: teacher?.name || teacher?.fullName || teacher?.displayName || "Unnamed teacher",
    email: teacher?.email || "",
    mobileNumber: teacher?.mobileNumber || teacher?.mobile_number || "",
    currentRoles: uniqueRoleNames(roleValues)
  };
};

const findSingleExactMatch = (items, query, getValue) => {
  const target = normaliseText(query);

  if (!target) {
    return null;
  }

  const matches = items.filter((item) => normaliseText(getValue(item)) === target);
  return matches.length === 1 ? matches[0] : null;
};

const roleNamesMatch = (roleName, comparedRoleName) =>
  normaliseText(roleName) === normaliseText(comparedRoleName);

const hasRoleName = (roleNames, comparedRoleName) =>
  roleNames.some((roleName) => roleNamesMatch(roleName, comparedRoleName));

const roleSetsMatch = (firstRoleNames, secondRoleNames) => {
  const firstKeys = uniqueRoleNames(firstRoleNames).map(normaliseText).sort();
  const secondKeys = uniqueRoleNames(secondRoleNames).map(normaliseText).sort();

  return firstKeys.length === secondKeys.length &&
    firstKeys.every((roleKey, index) => roleKey === secondKeys[index]);
};

const getExactRoleName = (roles, roleName) => {
  const match = findSingleExactMatch(roles, roleName, (role) => role.name);
  return match?.name || "";
};

const buildInitialSelectedRoleNames = (teacher, roles, prefillContext) => {
  let selectedRoleNames = [...(teacher?.currentRoles || [])];
  const replacementRoleNames = prefillContext.roleNames
    .map((roleName) => getExactRoleName(roles, roleName))
    .filter(Boolean);
  const removeRoleNames = [
    ...prefillContext.removeRoleNames,
    ...(prefillContext.currentRoleName && replacementRoleNames.length > 0
      ? [prefillContext.currentRoleName]
      : [])
  ];

  selectedRoleNames = selectedRoleNames.filter(
    (roleName) => !hasRoleName(removeRoleNames, roleName)
  );

  return uniqueRoleNames([...selectedRoleNames, ...replacementRoleNames]);
};

const buildRoleChange = (teacher, selectedRoleNames) => {
  const currentRoles = teacher?.currentRoles || [];
  const roles = uniqueRoleNames(selectedRoleNames);

  return {
    addedRoles: roles.filter((roleName) => !hasRoleName(currentRoles, roleName)),
    removedRoles: currentRoles.filter((roleName) => !hasRoleName(roles, roleName)),
    roles
  };
};

function UpdateTeacherRolesModal({
  show,
  handleClose,
  schoolid,
  prefill = null,
  onAssignAlpacaLead = null,
  embedded = false,
  onWorkflowComplete = null
}) {
  const [stage, setStage] = useState("form");
  const [teachers, setTeachers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [teacherSearchTerm, setTeacherSearchTerm] = useState("");
  const [roleSearchTerm, setRoleSearchTerm] = useState("");
  const [selectedTeacher, setSelectedTeacher] = useState(null);
  const [selectedRoleNames, setSelectedRoleNames] = useState([]);
  const [roleChange, setRoleChange] = useState({ roles: [], addedRoles: [], removedRoles: [] });
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const prefillContext = useMemo(() => normalisePrefill(prefill), [prefill]);

  const filteredTeachers = useMemo(() => {
    const query = normaliseText(teacherSearchTerm);

    if (!query) {
      return [];
    }

    return teachers.filter((teacher) =>
      [teacher.name, teacher.email, teacher.currentRoles.join(" ")]
        .some((value) => normaliseText(value).includes(query))
    );
  }, [teacherSearchTerm, teachers]);

  const filteredRoles = useMemo(() => {
    const query = normaliseText(roleSearchTerm);

    if (!query) {
      return roles;
    }

    return roles.filter((role) => normaliseText(role.name).includes(query));
  }, [roleSearchTerm, roles]);

  const rolesChanged = Boolean(
    selectedTeacher && !roleSetsMatch(selectedTeacher.currentRoles, selectedRoleNames)
  );
  const canSubmit = Boolean(selectedTeacher && rolesChanged && !isSubmitting);

  useEffect(() => {
    if (!show) {
      return;
    }

    setStage("form");
    setTeachers([]);
    setRoles([]);
    setTeacherSearchTerm(prefillContext.teacherName);
    setRoleSearchTerm(prefillContext.roleName);
    setSelectedTeacher(null);
    setSelectedRoleNames([]);
    setRoleChange({ roles: [], addedRoles: [], removedRoles: [] });
    setError("");

    if (!schoolid) {
      setError("Teachers could not be loaded. Please try again.");
      return;
    }

    setIsLoading(true);

    Promise.all([getTeachersBySchoolId(schoolid), getSchoolRoles(schoolid)])
      .then(([teachersResponse, rolesResponse]) => {
        const normalisedTeachers = (Array.isArray(teachersResponse) ? teachersResponse : [])
          .map(normaliseTeacher)
          .filter((teacher) => teacher.id && teacher.active !== false)
          .sort((teacherA, teacherB) => teacherA.name.localeCompare(teacherB.name));
        const roleItems = Array.isArray(rolesResponse?.data)
          ? rolesResponse.data
          : Array.isArray(rolesResponse)
            ? rolesResponse
            : [];
        const apiRoles = roleItems
          .map((role) => ({
            id: role?.id || role?._id || getRoleName(role),
            name: getRoleName(role),
            sortOrder: role?.sortOrder ?? 0
          }))
          .filter((role) => role.name);
        const roleNamesByKey = new Map(apiRoles.map((role) => [normaliseText(role.name), role]));
        normalisedTeachers.forEach((teacher) => {
          teacher.currentRoles.forEach((roleName) => {
            const key = normaliseText(roleName);
            if (!roleNamesByKey.has(key)) {
              roleNamesByKey.set(key, { id: `current-${key}`, name: roleName, sortOrder: 0 });
            }
          });
        });
        const normalisedRoles = Array.from(roleNamesByKey.values())
          .sort((roleA, roleB) => roleA.name.localeCompare(roleB.name));
        const prefilledTeacher = findSingleExactMatch(
          normalisedTeachers,
          prefillContext.teacherName,
          (teacher) => teacher.name
        );

        setTeachers(normalisedTeachers);
        setRoles(normalisedRoles);
        setSelectedTeacher(prefilledTeacher);
        setSelectedRoleNames(
          prefilledTeacher
            ? buildInitialSelectedRoleNames(prefilledTeacher, normalisedRoles, prefillContext)
            : []
        );
      })
      .catch((loadError) => {
        console.error("Error loading teacher roles workflow:", loadError);
        setError("Teachers and roles could not be loaded. Please try again.");
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [show, schoolid, prefillContext]);

  const handleTeacherSelect = (teacher) => {
    setSelectedTeacher(teacher);
    setSelectedRoleNames(buildInitialSelectedRoleNames(teacher, roles, prefillContext));
    setError("");
  };

  const handleRoleToggle = (role) => {
    setSelectedRoleNames((currentRoleNames) => {
      if (hasRoleName(currentRoleNames, role.name)) {
        return currentRoleNames.filter((roleName) => !roleNamesMatch(roleName, role.name));
      }

      return uniqueRoleNames([...currentRoleNames, role.name]);
    });
    setError("");
  };

  const handleSubmit = async () => {
    if (!canSubmit) {
      return;
    }

    const nextRoleChange = buildRoleChange(selectedTeacher, selectedRoleNames);

    setIsSubmitting(true);
    setError("");

    try {
      await updateTeacherProfileWithEmail(schoolid, selectedTeacher.id, {
        name: selectedTeacher.name,
        email: selectedTeacher.email,
        mobileNumber: selectedTeacher.mobileNumber || undefined,
        additionalRoles: nextRoleChange.roles
      });
      setRoleChange(nextRoleChange);
      setStage("success");
    } catch (submitError) {
      console.error("Error updating teacher role labels:", submitError);
      setError("The roles could not be updated. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForAnotherTeacher = () => {
    setStage("form");
    setTeacherSearchTerm("");
    setRoleSearchTerm("");
    setSelectedTeacher(null);
    setSelectedRoleNames([]);
    setRoleChange({ roles: [], addedRoles: [], removedRoles: [] });
    setError("");
  };

  const handleDone = () => {
    if (onWorkflowComplete) {
      onWorkflowComplete({
        reference: selectedTeacher?.email || selectedTeacher?.name || "",
        message: "Teacher roles updated."
      });
      return;
    }

    handleClose();
  };

  const handleModalHide = () => {
    if (stage === "success") {
      handleDone();
      return;
    }

    handleClose();
  };

  const renderTeacherResults = () => {
    if (isLoading) {
      return (
        <div className="update-teacher-role-search-state">
          <Spinner animation="border" size="sm" />
          <span>Loading teachers...</span>
        </div>
      );
    }

    if (!teacherSearchTerm.trim()) {
      return <p className="update-teacher-role-muted">Choose from teachers in this school.</p>;
    }

    if (filteredTeachers.length < 1) {
      return (
        <div className="update-teacher-role-empty">
          <strong>No matching teachers found.</strong>
          <span>Try another teacher name.</span>
        </div>
      );
    }

    return filteredTeachers.map((teacher) => {
      const selected = selectedTeacher?.id === teacher.id;

      return (
        <button
          key={teacher.id}
          type="button"
          className={`update-teacher-role-result${selected ? " selected" : ""}`}
          onClick={() => handleTeacherSelect(teacher)}
          aria-label={`Select ${teacher.name}`}
          aria-pressed={selected}
        >
          <span className="update-teacher-role-radio" aria-hidden="true" />
          <span className="update-teacher-role-result-copy">
            <strong>{teacher.name}</strong>
            <span>Current role{teacher.currentRoles.length === 1 ? "" : "s"}: {teacher.currentRoles.join(", ") || "None"}</span>
            <span className="update-teacher-role-pill">{teacher.currentRoles.length || 0} role{teacher.currentRoles.length === 1 ? "" : "s"}</span>
          </span>
          {selected && <span className="update-teacher-role-selected-label">Selected</span>}
        </button>
      );
    });
  };

  const renderRoleResults = () => {
    if (isLoading) {
      return (
        <div className="update-teacher-role-search-state">
          <Spinner animation="border" size="sm" />
          <span>Loading roles...</span>
        </div>
      );
    }

    if (filteredRoles.length < 1) {
      return (
        <div className="update-teacher-role-empty">
          <strong>No matching roles found.</strong>
          <span>Try another role label.</span>
        </div>
      );
    }

    return filteredRoles.map((role) => {
      const selected = hasRoleName(selectedRoleNames, role.name);
      const current = selectedTeacher?.currentRoles.some(
        (currentRole) => normaliseText(currentRole) === normaliseText(role.name)
      );

      return (
        <label
          key={role.id}
          className={`update-teacher-role-result${selected ? " selected" : ""}${current ? " current" : ""}`}
        >
          <input
            type="checkbox"
            checked={selected}
            onChange={() => handleRoleToggle(role)}
            aria-label={role.name}
          />
          <span className="update-teacher-role-result-copy">
            <strong>{role.name}</strong>
            <span className="update-teacher-role-pill">{current ? "Current role" : "Available role"}</span>
          </span>
        </label>
      );
    });
  };

  const renderSelectedRoles = () => {
    if (!selectedTeacher) {
      return null;
    }

    if (selectedRoleNames.length < 1) {
      return (
        <div className="update-teacher-role-selected-roles empty">
          No role labels selected.
        </div>
      );
    }

    return (
      <div className="update-teacher-role-selected-roles" aria-label="Current roles">
        {selectedTeacher.currentRoles.map((roleName) => (
          <span key={roleName} className="update-teacher-role-chip">{roleName}</span>
        ))}
      </div>
    );
  };

  const renderForm = () => (
    <>
      <Modal.Header closeButton>
        <Modal.Title as="h2">Update Teacher Roles</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="update-teacher-role-intro">Find the teacher and choose the role labels to update.</p>
        {error && <Alert variant="danger">{error}</Alert>}
        <Form.Group controlId="updateTeacherRoleTeacherSearch">
          <Form.Label>Which teacher needs a role update?</Form.Label>
          <Form.Text as="div" className="update-teacher-role-field-label">
            Search teacher
          </Form.Text>
          <Form.Control
            type="search"
            value={teacherSearchTerm}
            onChange={(event) => {
              setTeacherSearchTerm(event.target.value);
              setSelectedTeacher(null);
              setSelectedRoleNames([]);
              setError("");
            }}
            aria-label="Search teacher for role update"
          />
        </Form.Group>
        <div className="update-teacher-role-results" aria-label="Matching teachers">
          {renderTeacherResults()}
        </div>

        <div className="update-teacher-role-section">
          <Form.Label>Current roles</Form.Label>
          {renderSelectedRoles()}
          <Form.Group controlId="updateTeacherRoleRoleSearch">
            <Form.Label>
              {selectedTeacher ? `Which roles should ${selectedTeacher.name.split(" ")[0]} have?` : "Which roles should they have?"}
            </Form.Label>
            <Form.Text as="div" className="update-teacher-role-field-label">
              Filter roles
            </Form.Text>
            <Form.Control
              type="search"
              value={roleSearchTerm}
              onChange={(event) => {
                setRoleSearchTerm(event.target.value);
                setError("");
              }}
              aria-label="Filter role labels"
              disabled={!selectedTeacher}
            />
          </Form.Group>
          <div className="update-teacher-role-results" aria-label="Matching roles">
            {selectedTeacher ? renderRoleResults() : <p className="update-teacher-role-muted">Choose the teacher first.</p>}
          </div>
        </div>

        {selectedTeacher && (
          <Alert variant="warning" className="update-teacher-role-note">
            Unticked roles will be removed. Teacher details and existing class assignments will remain unchanged.
          </Alert>
        )}
      </Modal.Body>
      <Modal.Footer>
        <span className="update-teacher-role-footer-note">
          {selectedTeacher && rolesChanged
            ? `${selectedTeacher.name}'s role labels changed.`
            : selectedTeacher
              ? "Add or remove role labels."
              : "Choose the teacher first."}
        </span>
        <Button variant="outline-secondary" onClick={handleClose}>
          Cancel
        </Button>
        <Button variant="success" onClick={handleSubmit} disabled={!canSubmit}>
          {isSubmitting ? "Updating..." : "Update roles"}
        </Button>
      </Modal.Footer>
    </>
  );

  const renderSuccess = () => (
    <>
      <Modal.Header closeButton>
        <Modal.Title as="h2">Teacher Role Updated</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="update-teacher-role-intro">{selectedTeacher?.name} has been updated.</p>
        <Alert variant="success" className="update-teacher-role-success">
          <FaCheckCircle aria-hidden="true" />
          <span>
            <strong>{selectedTeacher?.name}'s role labels have been updated.</strong>
            <span>Teacher details and existing assignments were kept.</span>
          </span>
        </Alert>
        <div className="update-teacher-role-summary">
          <strong>What happened</strong>
          <span className="update-teacher-role-row">
            <span>Teacher</span>
            <span>{selectedTeacher?.name}</span>
          </span>
          <span className="update-teacher-role-row">
            <span>Roles added</span>
            <span>{roleChange.addedRoles.join(", ") || "None"}</span>
          </span>
          <span className="update-teacher-role-row">
            <span>Roles removed</span>
            <span>{roleChange.removedRoles.join(", ") || "None"}</span>
          </span>
          <span className="update-teacher-role-row">
            <span>Kept</span>
            <span>Teacher details and existing assignments.</span>
          </span>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <span className="update-teacher-role-footer-note">
          {embedded ? "Done returns to SENSAI." : "Roles updated."}
        </span>
        <Button variant="outline-secondary" onClick={resetForAnotherTeacher}>
          Update another teacher
        </Button>
        {onAssignAlpacaLead && (
          <Button
            variant="outline-secondary"
            onClick={() => onAssignAlpacaLead({
              teacherName: selectedTeacher?.name,
              email: selectedTeacher?.email,
              mobileNumber: selectedTeacher?.mobileNumber
            })}
          >
            Assign ALPACA Lead
          </Button>
        )}
        <Button variant="primary" onClick={handleDone}>
          Done
        </Button>
      </Modal.Footer>
    </>
  );

  return (
    <Modal
      show={show}
      onHide={handleModalHide}
      centered
      backdrop="static"
      className="update-teacher-role-modal"
    >
      {stage === "success" ? renderSuccess() : renderForm()}
    </Modal>
  );
}

export default UpdateTeacherRolesModal;
